import React, { useState } from "react";
import styled from "styled-components";
import { Braces, ChevronRight, Copy, RefreshCcw, Search, Wand2 } from "lucide-react";

const Page = styled.main`
  min-height: 100vh;
  padding: 110px 20px 90px;
  color: #e5e7eb;
  background:
    radial-gradient(900px at 12% 10%, rgba(245, 158, 11, 0.14), transparent 58%),
    radial-gradient(900px at 85% 12%, rgba(56, 189, 248, 0.14), transparent 60%),
    linear-gradient(180deg, #020617 0%, #04111f 100%);
`;

const Shell = styled.div`
  max-width: 1320px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 30px;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 0.84)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 32%);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.34);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  color: #f8fafc;
  font-size: clamp(36px, 5vw, 56px);
  line-height: 1.04;
`;

const Sub = styled.p`
  margin: 16px 0 0;
  max-width: 760px;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1.8;
`;

const Grid = styled.section`
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 14, 26, 0.92);
  overflow: hidden;
  min-width: 0;
`;

const Toolbar = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const PanelTitle = styled.div`
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 900;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative;
  min-width: 260px;

  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  min-height: 40px;
  padding: 0 14px 0 40px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #f8fafc;
  outline: none;
  font-size: 13px;

  &::placeholder {
    color: #64748b;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7dd3fc;
`;

const Button = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 12px;
  border: ${({ $primary }) => ($primary ? "none" : "1px solid rgba(148, 163, 184, 0.14)")};
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #f59e0b, #38bdf8)"
      : "rgba(15, 23, 42, 0.56)"};
  color: ${({ $primary }) => ($primary ? "#04111f" : "#f8fafc")};
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Editor = styled.textarea`
  width: 100%;
  height: 560px;
  border: none;
  outline: none;
  resize: vertical;
  background: rgba(2, 6, 23, 0.78);
  color: #dbeafe;
  padding: 18px;
  font-size: 14px;
  line-height: 1.75;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

  &::placeholder {
    color: #64748b;
  }

  @media (max-width: 960px) {
    height: 340px;
  }
`;

const Output = styled.div`
  height: 560px;
  padding: 18px;
  background: rgba(2, 6, 23, 0.78);
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.75;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  overflow: auto;

  @media (max-width: 960px) {
    height: 340px;
  }
`;

const TreeLine = styled.div`
  padding-left: ${({ $depth }) => `${$depth * 18}px`};
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.8;
  word-break: break-word;
`;

const Toggle = styled.button`
  width: 24px;
  height: 24px;
  margin-top: 1px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.48);
  color: #7dd3fc;
  cursor: pointer;
  transform: ${({ $open }) => ($open ? "rotate(90deg)" : "rotate(0deg)")};
  transition: transform 0.18s ease;
`;

const Spacer = styled.div`
  width: 24px;
  flex-shrink: 0;
`;

const KeyText = styled.span`
  color: #f8fafc;
`;

const StringText = styled.span`
  color: #86efac;
`;

const NumberText = styled.span`
  color: #fbbf24;
`;

const BooleanText = styled.span`
  color: #7dd3fc;
`;

const NullText = styled.span`
  color: #fda4af;
`;

const Highlight = styled.span`
  background: rgba(245, 158, 11, 0.2);
  color: #fde68a;
  border-radius: 6px;
  padding: 0 2px;
`;

const Status = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid
    ${({ $error }) => ($error ? "rgba(248, 113, 113, 0.28)" : "rgba(148, 163, 184, 0.12)")};
  background: ${({ $error }) =>
    $error ? "rgba(127, 29, 29, 0.28)" : "rgba(15, 23, 42, 0.52)"};
  color: ${({ $error }) => ($error ? "#fecaca" : "#94a3b8")};
  font-size: 13px;
  line-height: 1.7;
`;

const sampleJson = `{"name":"skilll.dev","features":["roadmaps","ai mentors","focus timer"],"active":true,"stats":{"daysCompleted":12,"streak":5}}`;

const isExpandable = (value) =>
  value && typeof value === "object" && Object.keys(value).length > 0;

const collectExpandedPaths = (value, basePath = "root") => {
  if (!value || typeof value !== "object") return {};

  const expanded = { [basePath]: true };
  const entries = Array.isArray(value) ? value : Object.values(value);

  entries.forEach((item, index) => {
    const path = `${basePath}.${index}`;
    if (item && typeof item === "object") {
      Object.assign(expanded, collectExpandedPaths(item, path));
    }
  });

  return expanded;
};

const matchesSearchText = (text, query) =>
  !query || String(text).toLowerCase().includes(query.toLowerCase());

const nodeMatchesSearch = (nodeKey, value, query) => {
  if (!query) return true;

  if (nodeKey !== null && matchesSearchText(nodeKey, query)) return true;

  if (!value || typeof value !== "object") {
    return matchesSearchText(value, query);
  }

  if (Array.isArray(value)) {
    return value.some((item) => nodeMatchesSearch(null, item, query));
  }

  return Object.entries(value).some(([key, item]) =>
    nodeMatchesSearch(key, item, query)
  );
};

function renderHighlightedText(text, query) {
  const raw = String(text);
  if (!query) return raw;

  const lowerRaw = raw.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts = [];
  let cursor = 0;

  while (cursor < raw.length) {
    const matchIndex = lowerRaw.indexOf(lowerQuery, cursor);
    if (matchIndex === -1) {
      parts.push(raw.slice(cursor));
      break;
    }

    if (matchIndex > cursor) {
      parts.push(raw.slice(cursor, matchIndex));
    }

    parts.push(
      <Highlight key={`${matchIndex}-${lowerQuery}`}>
        {raw.slice(matchIndex, matchIndex + query.length)}
      </Highlight>
    );
    cursor = matchIndex + query.length;
  }

  return parts;
}

function JsonNode({
  nodeKey,
  value,
  depth = 0,
  path = "root",
  expandedPaths,
  onToggle,
  searchQuery,
}) {
  if (!nodeMatchesSearch(nodeKey, value, searchQuery)) {
    return null;
  }

  const expandable = isExpandable(value);
  const isOpen = searchQuery ? true : expandedPaths[path] ?? true;
  const isArray = Array.isArray(value);

  const renderPrimitive = (primitiveValue) => {
    if (typeof primitiveValue === "string") {
      return <StringText>"{renderHighlightedText(primitiveValue, searchQuery)}"</StringText>;
    }

    if (typeof primitiveValue === "number") {
      return <NumberText>{renderHighlightedText(primitiveValue, searchQuery)}</NumberText>;
    }

    if (typeof primitiveValue === "boolean") {
      return <BooleanText>{renderHighlightedText(String(primitiveValue), searchQuery)}</BooleanText>;
    }

    if (primitiveValue === null) {
      return <NullText>null</NullText>;
    }

    return <span>{String(primitiveValue)}</span>;
  };

  if (!expandable) {
    return (
      <TreeLine $depth={depth}>
        <Spacer />
        {nodeKey !== null && <KeyText>"{renderHighlightedText(nodeKey, searchQuery)}"</KeyText>}
        {nodeKey !== null && <span>:</span>}
        {renderPrimitive(value)}
      </TreeLine>
    );
  }

  return (
    <>
      <TreeLine $depth={depth}>
        <Toggle onClick={() => onToggle(path)} $open={isOpen} aria-label={isOpen ? "Collapse" : "Expand"}>
          <ChevronRight size={16} />
        </Toggle>
        {nodeKey !== null && <KeyText>"{renderHighlightedText(nodeKey, searchQuery)}"</KeyText>}
        {nodeKey !== null && <span>:</span>}
        <span>{isArray ? "[" : "{"}</span>
        {!isOpen && (
          <>
            <span style={{ color: "#64748b" }}>
              {isArray ? `${value.length} items` : `${Object.keys(value).length} keys`}
            </span>
            <span>{isArray ? "]" : "}"}</span>
          </>
        )}
      </TreeLine>

      {isOpen &&
        (isArray
          ? value.map((item, index) => (
              <JsonNode
                key={`${path}.${index}`}
                nodeKey={null}
                value={item}
                depth={depth + 1}
                path={`${path}.${index}`}
                expandedPaths={expandedPaths}
                onToggle={onToggle}
                searchQuery={searchQuery}
              />
            ))
          : Object.entries(value).map(([childKey, item], index) => (
              <JsonNode
                key={`${path}.${childKey}`}
                nodeKey={childKey}
                value={item}
                depth={depth + 1}
                path={`${path}.${index}`}
                expandedPaths={expandedPaths}
                onToggle={onToggle}
                searchQuery={searchQuery}
              />
            )))}

      {isOpen && <TreeLine $depth={depth}><Spacer /><span>{isArray ? "]" : "}"}</span></TreeLine>}
    </>
  );
}

export default function JsonFormatterPage() {
  const [input, setInput] = useState(sampleJson);
  const [output, setOutput] = useState("");
  const [parsedJson, setParsedJson] = useState(null);
  const [expandedPaths, setExpandedPaths] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState("Paste minified or messy JSON and format it cleanly.");
  const [hasError, setHasError] = useState(false);

  const formatJson = () => {
    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, 2);
      setParsedJson(parsed);
      setOutput(formatted);
      setExpandedPaths(collectExpandedPaths(parsed));
      setStatus("JSON formatted successfully.");
      setHasError(false);
    } catch (error) {
      setParsedJson(null);
      setExpandedPaths({});
      setOutput("");
      setStatus(`Invalid JSON: ${error.message}`);
      setHasError(true);
    }
  };

  const resetAll = () => {
    setInput(sampleJson);
    setOutput("");
    setParsedJson(null);
    setExpandedPaths({});
    setSearchQuery("");
    setStatus("Editor reset. Click Format JSON to generate clean output.");
    setHasError(false);
  };

  const copyOutput = async () => {
    if (!output) {
      setStatus("Nothing formatted yet to copy.");
      setHasError(true);
      return;
    }

    try {
      await navigator.clipboard.writeText(output);
      setStatus("Formatted JSON copied to clipboard.");
      setHasError(false);
    } catch {
      setStatus("Could not copy formatted JSON from this browser session.");
      setHasError(true);
    }
  };

  const togglePath = (path) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  return (
    <Page>
      <Shell>
        <Hero>
          <Eyebrow>
            <Braces size={14} />
            Utility tool
          </Eyebrow>
          <Title>JSON Formatter</Title>
          <Sub>
            Paste raw JSON on the left, format it instantly, and copy clean output
            from the right. Built as a fast utility page inside the same product theme.
          </Sub>
          <Status $error={hasError}>{status}</Status>
        </Hero>

        <Grid>
          <Panel>
            <Toolbar>
              <PanelTitle>Input JSON</PanelTitle>
              <Actions>
                <Button onClick={resetAll}>
                  <RefreshCcw size={15} />
                  Reset
                </Button>
                <Button $primary onClick={formatJson}>
                  <Wand2 size={15} />
                  Format JSON
                </Button>
              </Actions>
            </Toolbar>
            <Editor
              value={input}
              onChange={(event) => setInput(event.target.value)}
              spellCheck={false}
              placeholder='{"hello":"world"}'
            />
          </Panel>

          <Panel>
            <Toolbar>
              <PanelTitle>Formatted Output</PanelTitle>
              <Actions>
                <SearchWrap>
                  <SearchIcon size={15} />
                  <SearchInput
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search key or value"
                  />
                </SearchWrap>
                <Button onClick={copyOutput}>
                  <Copy size={15} />
                  Copy
                </Button>
              </Actions>
            </Toolbar>
            <Output>
              {parsedJson ? (
                <JsonNode
                  nodeKey={null}
                  value={parsedJson}
                  expandedPaths={expandedPaths}
                  onToggle={togglePath}
                  searchQuery={searchQuery}
                />
              ) : (
                "Formatted JSON will appear here."
              )}
            </Output>
          </Panel>
        </Grid>
      </Shell>
    </Page>
  );
}
